import { useParams, Link, useSearchParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Check, Mail, ArrowRight, Loader2, XCircle, AlertTriangle } from "lucide-react";
import { api, formatISK, formatDate } from "@/lib/api";

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between py-4 border-b border-white/10">
      <span className="text-xs uppercase tracking-[0.15em] text-gray-500">{label}</span>
      <span data-testid={testid} className="text-sm text-white text-right">{value}</span>
    </div>
  );
}

export default function Confirmation() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const canceled = params.get("canceled") === "1";

  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);
  // 'checking' while awaiting payment, 'paid', 'canceled'
  const [payState, setPayState] = useState(canceled ? "canceled" : "checking");
  const [retrying, setRetrying] = useState(false);
  const pollRef = useRef(null);
  const attemptsRef = useRef(0);
  const MAX_POLLS = 90; // ~3 min at 2s interval

  const loadBooking = async () => {
    const res = await api.get(`/bookings/${bookingId}`);
    setBooking(res.data.booking);
    return res.data.booking;
  };

  const completePayment = async () => {
    setRetrying(true);
    try {
      const res = await api.post("/payments/checkout", { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (_) {
      setRetrying(false);
    }
  };

  useEffect(() => {
    loadBooking()
      .then((b) => {
        if (b.payment_status === "paid") {
          setPayState("paid");
        } else if (canceled) {
          setPayState("canceled");
        } else if (sessionId) {
          setPayState("checking");
          pollRef.current = setInterval(async () => {
            attemptsRef.current += 1;
            try {
              const st = await api.get(`/payments/status/${sessionId}`);
              if (st.data.payment_status === "paid") {
                clearInterval(pollRef.current);
                await loadBooking();
                setPayState("paid");
                return;
              }
            } catch (_) {}
            if (attemptsRef.current >= MAX_POLLS) {
              clearInterval(pollRef.current);
              setPayState("timeout");
            }
          }, 2000);
        } else {
          setPayState("checking");
        }
      })
      .catch(() => setError(true));
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  if (error) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-500 text-xs uppercase tracking-[0.3em]">Booking not found</div>;
  }
  if (!booking) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-500 text-xs uppercase tracking-[0.3em]">Loading…</div>;
  }

  const contactEmail = booking.type === "dinner" ? "catering@privatechef.is" : "viking@exclusivereykjavik.is";

  // Payment taking too long / abandoned — make it unambiguous the booking is NOT confirmed
  if (payState === "timeout") {
    return (
      <div data-testid="confirmation-page" className="max-w-2xl mx-auto px-6 md:px-12 py-20">
        <div className="w-16 h-16 border border-[#C44536]/50 flex items-center justify-center mb-8">
          <AlertTriangle size={26} className="text-[#C44536]" />
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4" data-testid="payment-timeout-label">Not Confirmed</p>
        <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.95]">Your booking isn't confirmed yet</h1>
        <p className="text-gray-400 mt-5 leading-relaxed">
          We haven't received your payment, so this booking is <span className="text-white font-semibold">not confirmed</span> and your spot is not yet held. You can complete your payment now to secure it.
        </p>
        <p className="text-gray-500 mt-3 text-sm leading-relaxed">
          Already completed payment? Your confirmation and QR code will arrive by email shortly — if you were charged but don't receive it, contact us at{" "}
          <a href={`mailto:${contactEmail}`} className="text-white underline">{contactEmail}</a>.
        </p>
        <div className="flex flex-wrap gap-4 mt-10">
          <button type="button" data-testid="conf-complete-payment" onClick={completePayment} disabled={retrying} className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-colors inline-flex items-center gap-3 disabled:opacity-60">{retrying ? <><Loader2 size={15} className="animate-spin" /> Redirecting…</> : <>Complete Payment <ArrowRight size={15} /></>}</button>
          <button type="button" data-testid="conf-recheck" onClick={() => window.location.reload()} className="bg-transparent text-white border border-white/25 hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-all">Check Again</button>
        </div>
      </div>
    );
  }

  // Payment canceled state
  if (payState === "canceled") {
    return (
      <div data-testid="confirmation-page" className="max-w-2xl mx-auto px-6 md:px-12 py-20">
        <div className="w-16 h-16 border border-[#C44536]/50 flex items-center justify-center mb-8">
          <XCircle size={28} className="text-[#C44536]" />
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4" data-testid="payment-canceled-label">Payment Cancelled</p>
        <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.95]">Your booking is not confirmed</h1>
        <p className="text-gray-400 mt-5 leading-relaxed">
          No payment was taken, so this booking is <span className="text-white font-semibold">not confirmed</span> and your spot is not held. You can complete your payment now to secure it, or start again.
        </p>
        <div className="flex flex-wrap gap-4 mt-10">
          <button type="button" data-testid="conf-complete-payment" onClick={completePayment} disabled={retrying} className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-colors inline-flex items-center gap-3 disabled:opacity-60">{retrying ? <><Loader2 size={15} className="animate-spin" /> Redirecting…</> : <>Complete Payment <ArrowRight size={15} /></>}</button>
          <Link to="/tours" data-testid="conf-retry" className="bg-transparent text-white border border-white/25 hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-all">Back to Tours</Link>
        </div>
      </div>
    );
  }

  // Awaiting payment confirmation
  if (payState === "checking") {
    return (
      <div data-testid="confirmation-page" className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <Loader2 size={32} className="animate-spin text-white mb-6" data-testid="payment-pending-spinner" />
        <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Finalizing your payment…</p>
        <p className="text-gray-600 text-sm mt-3 max-w-sm">Your booking isn't confirmed until payment is complete. Please wait a moment and don't close this page.</p>
      </div>
    );
  }

  const isTour = booking.type === "tour";
  const isBuggy = booking.type === "buggy";
  const isDinner = booking.type === "dinner";
  const charged = booking.amount_charged ?? booking.total;

  return (
    <div data-testid="confirmation-page" className="max-w-2xl mx-auto px-6 md:px-12 py-20">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="w-16 h-16 border border-white/20 flex items-center justify-center mb-8">
        <Check size={28} className="text-white" />
      </motion.div>

      <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4" data-testid="payment-paid-label">Confirmed · Paid</p>
      <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
        {isDinner ? "Your Table Is Reserved" : booking.tour_name}
      </h1>
      <p className="text-gray-400 mt-5 leading-relaxed">
        Thank you, {booking.customer.full_name}. A confirmation has been sent to {booking.customer.email}.
      </p>

      <div className="mt-12 bg-[#0C0C0E] border border-white/10 p-8">
        <Row label="Booking reference" value={booking.id.slice(0, 8).toUpperCase()} testid="conf-ref" />
        <Row label="Date" value={formatDate(booking.date)} testid="conf-date" />
        {isBuggy ? (
          <>
            <Row label="Time slot" value={booking.slot} testid="conf-slot" />
            <Row label="Setup" value={`${booking.rider_type} rider`} testid="conf-rider" />
            <Row label="Buggies" value={booking.buggies} testid="conf-buggies" />
            <Row label="Riders" value={booking.adults} testid="conf-adults" />
          </>
        ) : isTour ? (
          <>
            <Row label="Guests" value={booking.adults} testid="conf-adults" />
            {booking.children > 0 && <Row label="Children" value={booking.children} testid="conf-children" />}
          </>
        ) : (
          <>
            <Row label="Sitting" value={`${booking.seating_label || ""} ${booking.seating}`.trim()} testid="conf-seating" />
            <Row label="Party size" value={booking.party_size} testid="conf-party" />
            {booking.diets && <Row label="Menu" value={Object.entries(booking.diets).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(" · ") || "—"} testid="conf-menu" />}
          </>
        )}
        {booking.customer.allergies ? <Row label="Allergies" value={booking.customer.allergies} testid="conf-allergies" /> : null}
        {booking.card_fee > 0 && (
          <div className="flex justify-between py-4 border-b border-white/10">
            <span className="text-xs uppercase tracking-[0.15em] text-gray-500">Card fee incl.</span>
            <span data-testid="conf-fee" className="text-sm text-white text-right">{formatISK(booking.card_fee)}</span>
          </div>
        )}
        <div className="flex justify-between items-end pt-6">
          <span className="text-xs uppercase tracking-[0.15em] text-gray-400">Total paid</span>
          <span data-testid="conf-total" className="font-display font-extrabold text-3xl">{formatISK(charged)}</span>
        </div>
      </div>

      {booking.qr_data_url && (
        <div data-testid="conf-qr" className="mt-8 bg-white p-8 flex flex-col items-center">
          <img src={booking.qr_data_url} alt="Check-in QR code" className="w-48 h-48" />
          <p className="text-black text-xs uppercase tracking-[0.2em] mt-4 font-bold">Present this QR at check-in</p>
          <p className="text-gray-500 text-[11px] mt-1">Ref {booking.id.slice(0, 8).toUpperCase()}</p>
        </div>
      )}

      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.15em] text-gray-500 mt-6">
        <Mail size={14} /> A confirmation email with your QR code has been sent
      </div>

      <div className="flex flex-wrap gap-4 mt-10">
        <Link to="/tours" data-testid="conf-more-tours" className="bg-transparent text-white border border-white/25 hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-all">More Tours</Link>
        <Link to="/#the-table" data-testid="conf-dining" className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-colors inline-flex items-center gap-3">Discover Dining <ArrowRight size={15} /></Link>
      </div>
    </div>
  );
}
